import React from 'react';
import { FormattedMessage } from 'react-intl';
import {
	EasyClaimCaseResponseDto,
	EasyClaimAppState,
	EasyClaimDocumentType,
	DocumentFeature,
	ValidationResult,
} from 'src/easyclaim.api.client.typescript/src';
import styled from 'styled-components';

import { Image, SelectState } from '../../easyclaim.app.services/index';
import FormattedHtmlMessage from '../common/FormattedHtmlMessage';
import { FeedbackMode, ImageRecognitionFeedback2 } from '../common/ImageRecognitionFeedback2';
import { ImageRecognitionFeedbackPhotosInsteadWarning } from '../common/ImageRecognitionFeedbackPhotosInsteadWarning';
import { Modal } from '../common/Modal';

interface ImageRecognitionMismatchModalProps {
	images?: Image[];
	isOpen: boolean;
	onClose: () => void;
	onNext?: () => void;
	mileage: number | null;
	easyClaimCase: EasyClaimCaseResponseDto;
	screen: EasyClaimAppState;
}

interface ImageRecognitionMismatchModalState {}

export class ImageRecognitionMismatchModal extends React.Component<
	ImageRecognitionMismatchModalProps,
	ImageRecognitionMismatchModalState
> {
	private hasWarningOrError = (image: Image) => {
		return (
			!!image.uploadResult &&
			!!image.uploadResult.validationResults &&
			image.uploadResult.validationResults
				.filter((vr) => vr.feature !== DocumentFeature.ExpectedLimitedAmountOfAttempts)
				.some(
					(vr) =>
						vr.validationResult === ValidationResult.Error ||
						vr.validationResult === ValidationResult.Warning
				)
		);
	};

	private isVisible = (image: Image) => {
		const config = this.props.easyClaimCase._configuration;

		if (image.document?.documentType === EasyClaimDocumentType.DetailPicture) {
			return true;
		}

		const steps = config?.frontendSteps?.filter((vr) =>
			vr.controlsConfigurations?.some((c) => c.documentType === image.document?.documentType)
		);

		return steps === undefined ? false : steps.length > 0;
	};

	private mapProcessedImages = (mapFunc: (image: Image, index: number) => JSX.Element) => {
		return this.props.images
			?.filter((fs) => !!fs && this.hasWarningOrError(fs) && this.isVisible(fs))
			.map(mapFunc);
	};

	render() {
		const { isOpen } = this.props;

		return (
			<Modal
				isOpen={isOpen}
				closeOnBackdropClick={true}
				onClose={this.props.onClose}
				className="mismatch-image-modal"
				header={
					<span className="mismatch-modal-header">
						<FormattedHtmlMessage id="imageRecognitionMismatchModal.headline" />
					</span>
				}
				footer={
					<ButtonContainer className="ImageRecognitionMismatchModalButtonContainer">
						{this.props.images!.filter((fs) =>
							[SelectState.UnprocessableEntity, SelectState.UnsupportedMediaType].includes(
								fs.selectState
							)
						).length === 0 && (
							<button
								className="image-recognition-mismatch-modal-button-continue"
								onClick={this.props.onNext}
							>
								<FormattedMessage id="button.continue" />
							</button>
						)}
						<button
							className="image-recognition-mismatch-modal-button-secondary"
							onClick={this.props.onClose}
						>
							<FormattedMessage id="button.backchangeImage" />
						</button>
					</ButtonContainer>
				}
			>
				<div>
					{isOpen &&
						this.mapProcessedImages((image, index) => {
							return (
								<div key={image.document?.documentId ?? index}>
									<div className="image-recognition-mismatch-modal-original">
										<ImageRecognitionFeedback2
											validationId={image.componentId}
											uploadResult={image.uploadResult}
											feedbackMode={FeedbackMode.Modal}
										/>
										<StyledImage
											className="image-recognition-mismatch-modal-image"
											src={
												!!image?.dataUrlHead && !!image?.document?.fileContent
													? `${image?.dataUrlHead}, ${image?.document?.fileContent}`
													: undefined
											}
										/>
									</div>
									<div className="image-recognition-mismatch-modal">
										<StyledImage
											className="image-recognition-mismatch-modal-image"
											src={
												!!image?.dataUrlHead && !!image?.document?.fileContent
													? `${image?.dataUrlHead}, ${image?.document?.fileContent}`
													: undefined
											}
										/>
										<ImageRecognitionFeedback2
											validationId={image.componentId}
											uploadResult={image.uploadResult}
											feedbackMode={FeedbackMode.Modal}
										/>
									</div>
									<div className="image-recognition-mismatch-modal-photos-instead-warnings">
										<StyledImage
											className="image-recognition-mismatch-modal-image"
											src={
												!!image?.dataUrlHead && !!image?.document?.fileContent
													? `${image?.dataUrlHead}, ${image?.document?.fileContent}`
													: undefined
											}
										/>
										<ImageRecognitionFeedbackPhotosInsteadWarning
											image={image}
											feedbackMode={FeedbackMode.Modal}
											screen={this.props.screen}
										/>
									</div>
									{image.componentId == 'dashboardPicture' ? (
										<div className="image-recognition-mismatch-modal-entered-mileage">
											<div className="image-recognition-mismatch-modal-entered-mileage-title">
												<FormattedMessage id="mismatchModal.enteredMileage" />
											</div>
											<div className="image-recognition-mismatch-modal-entered-mileage-value">
												{this.props.mileage?.toString()}
											</div>
										</div>
									) : null}
								</div>
							);
						})}
				</div>
			</Modal>
		);
	}
}

const StyledImage = styled.img``;

const ButtonContainer = styled.p``;
